/**
 * Force preview: which ONE grid card should be playing its video preview right now.
 *
 * Rule: of the cards that are mostly on screen, the one whose centre is nearest the middle of the screen. To stop the choice
 * flickering while scrolling past two similar cards, the card that is already playing keeps playing until another is clearly
 * closer (it has to beat it by a margin).
 *
 * Pure functions only (no DOM, no state): the timer that measures the cards and the card that plays are in
 * hooks/use-force-preview.ts.
 */

export interface PreviewCandidate {
  key: string
  /** The card's top and bottom edge in the viewport (getBoundingClientRect). */
  top: number
  bottom: number
}

/** A card must have at least this much of its height on screen to be a candidate. */
export const MIN_VISIBLE_FRACTION = 0.5

/** A newcomer must be closer to the centre than the current winner by this fraction of the viewport height to take over. */
export const SWITCH_MARGIN_FRACTION = 0.08

export function visibleFraction(candidate: PreviewCandidate, viewportHeight: number): number {
  const height = candidate.bottom - candidate.top
  if (height <= 0 || viewportHeight <= 0) return 0
  const visible = Math.min(candidate.bottom, viewportHeight) - Math.max(candidate.top, 0)
  return Math.max(0, visible) / height
}

const distanceFromCentre = (candidate: PreviewCandidate, viewportHeight: number) =>
  Math.abs((candidate.top + candidate.bottom) / 2 - viewportHeight / 2)

export function pickPreviewWinner(candidates: PreviewCandidate[], viewportHeight: number, current: string | null = null): string | null {
  const eligible = candidates.filter((candidate) => visibleFraction(candidate, viewportHeight) >= MIN_VISIBLE_FRACTION)
  if (eligible.length === 0) return null

  let best = eligible[0]
  let bestDistance = distanceFromCentre(best, viewportHeight)
  for (const candidate of eligible.slice(1)) {
    const distance = distanceFromCentre(candidate, viewportHeight)
    if (distance < bestDistance || (distance === bestDistance && candidate.key < best.key)) {
      best = candidate
      bestDistance = distance
    }
  }

  const incumbent = current ? eligible.find((candidate) => candidate.key === current) : undefined
  if (incumbent && incumbent.key !== best.key) {
    const margin = viewportHeight * SWITCH_MARGIN_FRACTION
    if (distanceFromCentre(incumbent, viewportHeight) - bestDistance < margin) return incumbent.key
  }
  return best.key
}
